import { Button, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import ApproveRequestDialogIcon from "@entrilia/shared/icons/ApproveRequestDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../api/adminApi";
import type { Approval } from "../../api/types/approvalTypes";

const approveBulkApprovals = withErrorHandling(adminApi.approveBulkApprovals);

interface Props {
  approvalIds: string[];
  prompt: string;
  onClose: () => void;
  onApply: (approvals: Approval[]) => void;
}

const BulkApprovalDialog = ({ approvalIds, prompt, onClose, onApply }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [saving, setSaving] = useState(false);
  const [comment, setComment] = useState("");

  const handleApply = async () => {
    setSaving(true);

    const [approvals, error] = await approveBulkApprovals({
      approvalIds,
      commentUserInput: comment.trim() || undefined,
    });

    setSaving(false);

    if (error) {
      logError(error, "[BulkApprovalDialog] approveBulkApprovals");
      sendNotificationError(error.message);
      return;
    }

    sendNotification(`${approvals.length} request(s) have been approved`);
    onApply(approvals);
    onClose();
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Approve Requests</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <ApproveRequestDialogIcon sx={{ width: 80, height: 80 }} />

          <Typography variant="h6" sx={{ textAlign: "center" }}>
            {prompt}
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={5}
            label="Comment (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="contained" color="primary" loading={saving} onClick={handleApply}>
          Approve
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default BulkApprovalDialog;
